"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { t } from "@/lib/i18n/interpolate";
import { parsePastedTable } from "@/lib/tool-kit/developer/csv-table";

type Align = "left" | "center" | "right";
type InputMode = "grid" | "paste";
interface GridRow {
  id: string;
  cells: string[];
}

const PIPE_RE = /\|/g;

type Strings = {
  [K in keyof typeof MARKDOWN_TABLE_GENERATOR_STRINGS]: string;
};

const ALIGN_OPTIONS: { value: Align; label: keyof Strings }[] = [
  { value: "left", label: "alignLeft" },
  { value: "center", label: "alignCenter" },
  { value: "right", label: "alignRight" },
];

let idCounter = 0;

function newId(): string {
  idCounter += 1;
  return `k${idCounter}`;
}

function makeRow(cols: number, cells?: string[]): GridRow {
  return {
    id: newId(),
    cells: Array.from({ length: cols }, (_, i) => cells?.[i] ?? ""),
  };
}

function escapeCell(cell: string): string {
  return cell.trim().replace(PIPE_RE, "\\|");
}

function padCell(text: string, width: number, align: Align): string {
  if (align === "right") {
    return text.padStart(width);
  }
  if (align === "center") {
    const spare = Math.max(width - text.length, 0);
    const left = Math.floor(spare / 2);
    return `${" ".repeat(left)}${text}`.padEnd(width);
  }
  return text.padEnd(width);
}

function separatorCell(width: number, align: Align): string {
  if (align === "center") {
    return `:${"-".repeat(Math.max(width - 2, 1))}:`;
  }
  if (align === "right") {
    return `${"-".repeat(Math.max(width - 1, 2))}:`;
  }
  return `:${"-".repeat(Math.max(width - 1, 2))}`;
}

function getAlign(aligns: Align[], index: number): Align {
  return aligns[index] ?? "left";
}

function buildMarkdownTable(rows: string[][], aligns: Align[]): string {
  if (rows.length === 0) {
    return "";
  }
  const colCount = Math.max(...rows.map((r) => r.length));
  const norm = rows.map((row) =>
    Array.from({ length: colCount }, (_, i) => escapeCell(row[i] ?? ""))
  );
  const widths = Array.from({ length: colCount }, (_, i) =>
    Math.max(3, ...norm.map((row) => row[i]?.length ?? 0))
  );
  const toLine = (cells: string[]): string => `| ${cells.join(" | ")} |`;
  const padRow = (row: string[]): string =>
    toLine(
      row.map((cell, i) => padCell(cell, widths[i] ?? 3, getAlign(aligns, i)))
    );
  const headerLine = padRow(norm[0] ?? []);
  const separatorLine = toLine(
    widths.map((w, i) => separatorCell(w, getAlign(aligns, i)))
  );
  const bodyLines = norm.slice(1).map(padRow);
  return [headerLine, separatorLine, ...bodyLines].join("\n");
}

function previewAlignClass(align: Align): string {
  if (align === "center") {
    return "text-center";
  }
  if (align === "right") {
    return "text-right";
  }
  return "text-left";
}

const INITIAL_COL_IDS = [newId(), newId(), newId()];
const INITIAL_ROWS = [
  makeRow(3, ["Header 1", "Header 2", "Header 3"]),
  makeRow(3),
  makeRow(3),
];

/** English strings. A translated content file overrides these through `ui`. */
export const MARKDOWN_TABLE_GENERATOR_STRINGS = {
  inputMode: "Input mode",
  visualGridEditor: "Visual grid editor",
  pasteCsvTsv: "Paste CSV / TSV",
  rows: "Rows",
  removeRow: "Remove row",
  addRow: "Add row",
  columns: "Columns",
  removeColumn: "Remove column",
  addColumn: "Add column",
  theFirstRowIsThe: "The first row is the table header.",
  csvOrTsvDataThe: "CSV or TSV data — the delimiter is detected automatically",
  columnAlignment: "Column alignment",
  col: "Col ",
  markdown: "Markdown",
  copied: "Copied!",
  copyMarkdown: "Copy Markdown",
  livePreview: "Live Preview",
  alignLeft: "Left",
  alignCenter: "Center",
  alignRight: "Right",
  headerColumnAriaLabel: "Header column {column}",
  rowColumnAriaLabel: "Row {row} column {column}",
  headerPlaceholder: "Header",
} as const;

export default function MarkdownTableGeneratorWidget() {
  const [mode, setMode] = useState<InputMode>("grid");
  const [colIds, setColIds] = useState<string[]>(INITIAL_COL_IDS);
  const [gridRows, setGridRows] = useState<GridRow[]>(INITIAL_ROWS);
  const [pasteInput, setPasteInput] = useState("");
  const [aligns, setAligns] = useState<Align[]>([]);
  const [copied, setCopied] = useState(false);

  const rows =
    mode === "grid"
      ? gridRows.map((r) => r.cells)
      : parsePastedTable(pasteInput);
  const colCount = rows.length > 0 ? Math.max(...rows.map((r) => r.length)) : 0;
  const hasContent = rows.some((row) => row.some((c) => c.trim().length > 0));
  const markdown = hasContent ? buildMarkdownTable(rows, aligns) : "";
  const columnIndexes = Array.from({ length: colCount }, (_, i) => i);

  function updateCell(rowIndex: number, colIndex: number, value: string) {
    setGridRows((prev) =>
      prev.map((row, ri) => {
        if (ri !== rowIndex) {
          return row;
        }
        const cells = row.cells.slice();
        cells[colIndex] = value;
        return { ...row, cells };
      })
    );
  }

  function setAlignAt(index: number, value: Align) {
    setAligns((prev) => {
      const next = prev.slice();
      while (next.length <= index) {
        next.push("left");
      }
      next[index] = value;
      return next;
    });
  }

  function addRow() {
    setGridRows((prev) => [...prev, makeRow(colIds.length)]);
  }

  function removeRow() {
    setGridRows((prev) => (prev.length > 2 ? prev.slice(0, -1) : prev));
  }

  function addColumn() {
    setColIds((prev) => [...prev, newId()]);
    setGridRows((prev) =>
      prev.map((row) => ({ ...row, cells: [...row.cells, ""] }))
    );
  }

  function removeColumn() {
    if (colIds.length <= 1) {
      return;
    }
    setColIds((prev) => prev.slice(0, -1));
    setGridRows((prev) =>
      prev.map((row) => ({ ...row, cells: row.cells.slice(0, -1) }))
    );
  }

  function handleCopy() {
    if (markdown.length === 0) {
      return;
    }
    writeToClipboard(markdown).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const strings = useToolUi(MARKDOWN_TABLE_GENERATOR_STRINGS);

  return (
    <>
      {/* Input mode */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.inputMode}
        </p>
        <div className="flex flex-wrap gap-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={mode === "grid"}
              className="accent-primary"
              name="input-mode"
              onChange={() => setMode("grid")}
              type="radio"
              value="grid"
            />
            {strings.visualGridEditor}
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={mode === "paste"}
              className="accent-primary"
              name="input-mode"
              onChange={() => setMode("paste")}
              type="radio"
              value="paste"
            />
            {strings.pasteCsvTsv}
          </label>
        </div>
      </section>
      {/* Grid editor */}
      {mode === "grid" && (
        <section className="space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-foreground text-sm">
              {strings.rows}
              <button
                aria-label={strings.removeRow}
                className="rounded-lg border border-border bg-card px-3 py-1.5 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
                disabled={gridRows.length <= 2}
                onClick={removeRow}
                type="button"
              >
                −
              </button>
              <span className="w-6 text-center font-medium">
                {gridRows.length}
              </span>
              <button
                aria-label={strings.addRow}
                className="rounded-lg border border-border bg-card px-3 py-1.5 font-medium text-foreground text-sm transition-colors hover:bg-muted"
                onClick={addRow}
                type="button"
              >
                +
              </button>
            </div>
            <div className="flex items-center gap-2 text-foreground text-sm">
              {strings.columns}
              <button
                aria-label={strings.removeColumn}
                className="rounded-lg border border-border bg-card px-3 py-1.5 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
                disabled={colIds.length <= 1}
                onClick={removeColumn}
                type="button"
              >
                −
              </button>
              <span className="w-6 text-center font-medium">
                {colIds.length}
              </span>
              <button
                aria-label={strings.addColumn}
                className="rounded-lg border border-border bg-card px-3 py-1.5 font-medium text-foreground text-sm transition-colors hover:bg-muted"
                onClick={addColumn}
                type="button"
              >
                +
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <div className="min-w-fit space-y-2">
              {gridRows.map((row, ri) => (
                <div className="flex gap-2" key={row.id}>
                  {colIds.map((colId, ci) => (
                    <input
                      aria-label={
                        ri === 0
                          ? t(strings.headerColumnAriaLabel, {
                              column: String(ci + 1),
                            })
                          : t(strings.rowColumnAriaLabel, {
                              row: String(ri),
                              column: String(ci + 1),
                            })
                      }
                      className={`w-36 rounded-lg border border-border bg-card px-3 py-2 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary ${ri === 0 ? "font-medium" : ""}`}
                      key={colId}
                      onChange={(e) => updateCell(ri, ci, e.target.value)}
                      placeholder={ri === 0 ? strings.headerPlaceholder : ""}
                      type="text"
                      value={row.cells[ci] ?? ""}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
          <p className="text-muted-foreground text-xs">
            {strings.theFirstRowIsThe}
          </p>
        </section>
      )}
      {/* Paste input */}
      {mode === "paste" && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="paste-input"
          >
            {strings.csvOrTsvDataThe}
          </label>
          <textarea
            className="min-h-[180px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            id="paste-input"
            onChange={(e) => setPasteInput(e.target.value)}
            placeholder={
              "Name,Role,City\nAlice,Editor,Boston\nBob,Writer,Austin"
            }
            value={pasteInput}
          />
        </div>
      )}
      {/* Alignment */}
      {colCount > 0 && (
        <section className="space-y-3">
          <p className="font-medium text-foreground text-sm">
            {strings.columnAlignment}
          </p>
          <div className="flex flex-wrap gap-4">
            {columnIndexes.map((i) => (
              <label
                className="flex items-center gap-2 text-foreground text-sm"
                key={colIds[i] ?? `paste-col-${i}`}
              >
                {strings.col}
                {i + 1}
                <select
                  className="rounded-lg border border-border bg-card px-2 py-1.5 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  onChange={(e) => setAlignAt(i, e.target.value as Align)}
                  value={getAlign(aligns, i)}
                >
                  {ALIGN_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {strings[o.label]}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        </section>
      )}
      {/* Markdown output */}
      {markdown.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-foreground text-lg">
              {strings.markdown}
            </h2>
            <button
              className="rounded-lg border border-border bg-card px-4 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted"
              onClick={handleCopy}
              type="button"
            >
              {copied ? strings.copied : strings.copyMarkdown}
            </button>
          </div>
          <textarea
            className="min-h-[160px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="markdown-output"
            readOnly
            value={markdown}
          />
        </section>
      )}
      {/* Preview */}
      {markdown.length > 0 && rows.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.livePreview}
          </h2>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  {(rows[0] ?? []).map((cell, ci) => (
                    <th
                      className={`border-border border-b px-4 py-2 font-medium text-foreground ${previewAlignClass(getAlign(aligns, ci))}`}
                      key={colIds[ci] ?? `h-${cell}-${ci}`}
                    >
                      {cell}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.slice(1).map((row) => (
                  <tr
                    className="border-border border-t bg-card odd:bg-card even:bg-muted/20"
                    key={row.join("\x00")}
                  >
                    {row.map((cell, ci) => (
                      <td
                        className={`px-4 py-2 text-muted-foreground ${previewAlignClass(getAlign(aligns, ci))}`}
                        key={`${rows[0]?.[ci] ?? ci}-${cell}`}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}
